import { CollectionConfig } from "@rebasepro/types";
import talentApplicationsCollection from "./talent_applications";
import { HIDDEN_FROM_FORM } from "./_relation_display";
import { deriveTalentCanon } from "../taxonomy/derive";
import { ANY_JOB_FUNCTION, JOB_FUNCTIONS } from "../taxonomy/job-functions";

const talentsCollection: CollectionConfig = {
    name: "Candidatos",
    singularName: "Candidato",
    slug: "talents",
    table: "talents",
    // Candidate PII (email, phone, cv_url, questionnaire). The previous rule
    // granted `all` to every signed-in user, which meant any registered talent
    // could read — and update or delete — every other talent's row.
    securityRules: [
        {
            name: "talent_own_row",
            operation: "all",
            using: "auth.uid() = auth_user_id",
            withCheck: "auth.uid() = auth_user_id"
        },
        {
            // An employer reads the candidates who applied to it, and nobody
            // else. Until drizzle/0060 this granted every company member the
            // whole pool — 300 people's email, phone and questionnaire behind
            // a self-service registration — for a browse-the-talent tab that
            // logged zero profile views in its lifetime. Employers do not
            // browse the pool: the team presents candidates (PRODUCT.md). Two
            // acts open a person's row to a company — they applied to it
            // (this rule), or the team presented them to it (the next).
            //
            // The employer dashboard's applications list needs exactly this
            // much: it resolves each applicant's name, email and LinkedIn from
            // this table — by `auth_user_id` for a candidatura sent from the
            // site, by `talent_id` for one the team registered from an email
            // in Correo, which has no account behind it (drizzle/0072).
            //
            // The `talent_id` branch holds only when `ta.auth_user_id IS
            // NULL`, and that condition is the security. A candidate may
            // insert their own application naming any `talent_id` (their rule
            // checks only `auth_user_id`), so a bare `talent_id` match would
            // let someone holding a company and a candidate account read
            // anybody. A row with no account is written by an admin only, and
            // `talent_applications_only_status_for_others` (0064) stops a
            // company blanking `auth_user_id` or re-pointing `talent_id`.
            name: "company_read_own_applicants",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM talent_applications ta JOIN company_members cm ON cm.company_id = ta.company_id WHERE cm.auth_user_id = auth.uid() AND (ta.auth_user_id = (talents.auth_user_id)::text OR (ta.auth_user_id IS NULL AND ta.talent_id = talents.id)))"
        },
        {
            // …or whom the team presented to it (drizzle/0069). The other half
            // of the same sentence in PRODUCT.md: everyone who did not apply
            // reaches an employer as somebody a person chose and sent, and a
            // `presented_candidates` row is that choice written down. Postgres
            // ORs permissive policies, so this is the OR clause beside the one
            // above, kept separate so each name says what it opens.
            //
            // Only an admin writes a presentation, and the trigger on that
            // table stops a company from pointing its own presentation at
            // somebody else — which is what keeps this join from being a way
            // to read anyone.
            name: "company_read_presented_candidates",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM presented_candidates pc JOIN company_members cm ON cm.company_id = pc.company_id WHERE cm.auth_user_id = auth.uid() AND pc.talent_id = talents.id)"
        },
        {
            // The two rules above are what an employer may read; this one makes
            // it the *most* an employer may read, whatever else is on the table.
            // Restrictive, so it is ANDed with every permissive policy — the
            // old `recruiter_read_candidates` (the whole pool to any company
            // member) included, which a backend on an older revision re-creates
            // on every boot and a newer one never drops. That happened: 0060
            // removed it on 2026-09-21 and a cold start put it back in
            // production the next day. See drizzle/0071.
            //
            // Anyone who is not a company member passes untouched, so this
            // changes nothing for candidates, admins or the server context.
            name: "employers_read_only_their_candidates",
            operation: "select",
            mode: "restrictive",
            using: "auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR auth.uid() = (auth_user_id)::text OR NOT EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid()) OR EXISTS (SELECT 1 FROM talent_applications ta JOIN company_members cm ON cm.company_id = ta.company_id WHERE cm.auth_user_id = auth.uid() AND (ta.auth_user_id = (talents.auth_user_id)::text OR (ta.auth_user_id IS NULL AND ta.talent_id = talents.id))) OR EXISTS (SELECT 1 FROM presented_candidates pc JOIN company_members cm ON cm.company_id = pc.company_id WHERE cm.auth_user_id = auth.uid() AND pc.talent_id = talents.id)"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],

    /**
     * Derive `certifications_canon` and `skills_canon` from the questionnaire,
     * on every write. The candidate's own answers are never touched — see
     * `taxonomy/derive.ts`.
     */
    callbacks: {
        beforeSave: ({ values }) => ({ ...values, ...deriveTalentCanon(values) })
    },
    // Sourcing is the team's product. Without this block `.search()` is
    // `ILIKE '%term%'` over top-level string columns only — which for this
    // collection means name, email, location and the objective, and *nothing*
    // inside `questionnaire`. Every certification, degree and course a
    // candidate entered was unreachable from the search box: a recruiter typing
    // "auditor de iso14000" got zero results against a pool that holds several
    // ISO 14001 auditors, and no error to explain why.
    //
    // Four separate things in that one query were broken, and each option below
    // is one of them: reaching into the JSONB at all, matching `auditor` to
    // `auditoría`, dropping `de` and AND-ing the rest, and getting `iso14000`
    // close enough to `ISO 14001`.
    search: {
        // The content is Spanish: stems `auditores`/`auditoría` onto one lexeme
        // and treats `de`, `en`, `y` as stopwords rather than required terms.
        language: "spanish",
        // Not cosmetic here. Postgres stems `auditoría` to `auditor` and
        // `auditoria` to `auditori` — different lexemes — so without this a
        // recruiter typing without accents (most of them, most of the time)
        // misses every candidate who typed with them.
        unaccent: true,
        // Standards get written a dozen ways: "ISO 14001", "ISO14001",
        // "iso 14001:2015". Trigram similarity is what carries a near-miss
        // across; stemming never will, since they are simply different lexemes.
        fuzzy: true,
        fields: [
            { path: "full_name", weight: "A" },
            // The certifications are the point of the whole block.
            { path: "questionnaire.certifications", weight: "A" },
            // Skills alongside them, and at the same weight: a recruiter
            // searching "power bi" or "huella de carbono" is naming the thing
            // they need done, which is as identifying as a credential. Also the
            // only index that reaches what people have been putting in the
            // wrong box for a year — "qgis", "Project management" and
            // "Economía Circular" are all live under certifications.
            { path: "questionnaire.skills", weight: "A" },
            { path: "questionnaire.degree", weight: "B" },
            { path: "questionnaire.other_studies", weight: "B" },
            // Free text the candidate wrote about themselves — real signal, but
            // long, so it should not outrank a named certification.
            { path: "professional_objective", weight: "C" },
            { path: "questionnaire.career_motivation", weight: "D" },
            // Where they are. `location` is the legacy top-level column and
            // `questionnaire.city` is where the wizard writes now; both are
            // populated across the pool, so both are indexed.
            { path: "location", weight: "C" },
            { path: "questionnaire.city", weight: "C" }
        ]
        // Deliberately NOT indexed: `sustainability_areas` (a map of
        // area → level whose values are ids like "advanced", so every candidate
        // would match a search for "advanced"), the `years_experience_*` and
        // `country` enums (fixed vocabularies, already filterable, and an enum
        // is refused by the block anyway), and `interests`, whose values are
        // option ids rather than words anyone would type.
    },
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        auth_user_id: {
            name: "ID de usuario",
            type: "string",
            userSelect: true,
            description: "Vínculo con la cuenta de acceso. Una fila por persona — único. Vacío si el lead se captó antes de que se registrara.",
            /**
             * Not editable here, and not because the form would break.
             *
             * This column is who the candidate signs in as: `talent_own_row`
             * authorises every read and write they make against it, and
             * `talent_applications.beforeSave` resolves applications through
             * it. Retyping it in the admin does not "correct a link" — it hands
             * one person's profile, questionnaire and applications to another
             * account, silently and with no way to tell afterwards that it was
             * ever anyone else's.
             *
             * Since drizzle/0048 the database enforces that the value points at
             * a real account, which stops the field from pointing at nothing.
             * It does not stop it from pointing at the wrong person, and no
             * constraint can: both values are equally valid accounts. So the
             * field is readable — you can see who a candidate is — and is
             * changed by the flows that own it, never typed.
             */
            admin: { readOnly: true },
            validation: {
                required: false,
                unique: true
            }
        },
        full_name: {
            name: "Nombre completo",
            type: "string",
            validation: {
                required: true
            }
        },
        email: {
            name: "Email",
            type: "string",
            validation: {
                required: true
            }
        },
        phone: {
            name: "Teléfono",
            type: "string"
        },
        linkedin_url: {
            name: "LinkedIn",
            type: "string"
        },
        cv_url: {
            name: "CV",
            type: "string",
            /**
             * The file, not a link to one.
             *
             * This was a plain text field holding a URL somebody had to host
             * elsewhere, which meant the CV was only ever as durable as
             * whatever Drive folder or WeTransfer link it pointed at — and for
             * a CV-imported candidate there is no such link at all, because the
             * PDF arrived as an upload and was read and dropped.
             *
             * `public: false` is stated rather than left to the default: a CV
             * is the most sensitive thing this table holds — full name, phone,
             * address history, employers — and a public object is served from a
             * stable, token-less, CDN-cached URL that anyone who ever sees it
             * keeps. These are served as short-lived signed URLs instead.
             */
            storage: {
                public: false,
                acceptedFiles: ["application/pdf", "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
                /**
                 * Enforced by the server for any upload that names this
                 * property, which the candidate's own upload on the site does
                 * (`src/common/cv.ts` — PDF only there, same ceiling). A CV is
                 * a few hundred kilobytes; ten megabytes leaves room for a
                 * scanned one and none for a video somebody mislabelled.
                 */
                maxSize: 10 * 1024 * 1024,
                /**
                 * First segment is the owner, because that is exactly what
                 * `storageAuthorize` in backend/src/index.ts reads: it splits
                 * the key and grants access to `ownerId === user.uid`, or to an
                 * admin. So a registered candidate can fetch their own CV and
                 * nobody else's — and a company can fetch it only once they
                 * have applied to it, and only while it is the PDF on this row
                 * (backend/src/services/cv-access.ts).
                 *
                 * A CV-imported candidate has no `auth_user_id` — nobody
                 * registered — so there is no uid to own it and the file goes
                 * under `talents/<row id>`, which matches no `user.uid` and is
                 * therefore admin-only. That is the correct answer for a
                 * document the subject never uploaded, and it stops being the
                 * answer the moment an account is linked and a new file is
                 * uploaded under their uid.
                 */
                storagePath: ({ values, entityId }) => {
                    const owner = typeof values?.auth_user_id === "string" && values.auth_user_id
                        ? values.auth_user_id
                        : `talents/${entityId ?? "sin-id"}`;
                    return `${owner}/cv`;
                },
                fileName: ({ file }) => `${Date.now()}-${file.name}`
            }
        },
        photo_url: {
            name: "Foto",
            type: "string",
            /**
             * Private, for the same reason the CV above is.
             *
             * A public object is served from a stable, token-less, CDN-cached
             * URL that anyone who ever sees it keeps — and a face is the one
             * field here that identifies somebody to a stranger who has no
             * other detail about them. `storageAuthorize` in
             * backend/src/index.ts grants the owner and admins — the
             * candidate, and the recruiters working the queue — and, read
             * only, a member of a company the candidate applied to, for the
             * photo this row names now (backend/src/services/photo-access.ts,
             * the CV's join). Short-lived signed URLs, resolved on read.
             *
             * The path mirrors `cv_url`'s exactly rather than being derived
             * separately, because it is the same authorization rule doing the
             * work — first segment is the owner. A candidate with no account
             * (a CV import) has no uid, so the file lands under
             * `talents/<row id>`, which matches no `user.uid` and is therefore
             * admin-only.
             */
            storage: {
                public: false,
                acceptedFiles: ["image/jpeg", "image/png", "image/webp"],
                storagePath: ({ values, entityId }) => {
                    const owner = typeof values?.auth_user_id === "string" && values.auth_user_id
                        ? values.auth_user_id
                        : `talents/${entityId ?? "sin-id"}`;
                    return `${owner}/photo`;
                },
                fileName: ({ file }) => `${Date.now()}-${file.name}`
            }
        },
        photo_source: {
            name: "Origen de la foto",
            type: "string",
            /**
             * Read-only in the admin: this records how the photo arrived, and a
             * recruiter editing it would be rewriting the provenance of
             * somebody else's personal data rather than correcting a fact.
             * Written by the registration form and by
             * `functions/import-linkedin-photo.ts`.
             */
            admin: { readOnly: true },
            enum: [
                { id: "upload", label: "Subida por la persona", color: "green" },
                { id: "linkedin", label: "Importada de LinkedIn", color: "blue" }
            ]
        },
        location: {
            name: "Ubicación",
            type: "string"
        },
        interests: {
            name: "Áreas de interés",
            type: "array",
            columnType: "text[]",
            of: {
                name: "Área",
                type: "string",
                enum: [
                    { id: "job_search", label: "Búsqueda de empleo y nuevas oportunidades" },
                    { id: "profile_interview", label: "Optimización de perfil y preparación de entrevistas" },
                    { id: "career_guidance", label: "Orientación y reinvención" },
                    { id: "training_skills", label: "Formación y desarrollo de competencias" }
                ]
            }
        },
        professional_objective: {
            name: "Objetivo profesional",
            type: "string",
            admin: {
                multiline: true
            }
        },
        privacy_accepted: {
            name: "Privacidad aceptada",
            type: "boolean",
            validation: {
                required: true
            }
        },
        questionnaire: {
            name: "Cuestionario Profesional",
            type: "map",
            // The generic map field renders these twenty-odd inputs in one
            // unbroken column in declaration order — structurally faithful and
            // unusable, since reaching "disponibilidad para viajar" means
            // scrolling past certifications and salary and nothing on screen
            // says the questionnaire has parts. The custom field groups them
            // into the sections the candidate answered, and changes nothing
            // else: each field is still rendered by `PropertyFieldBinding`, so
            // validation, descriptions and `sustainability_areas`' own custom
            // field all behave exactly as before.
            admin: { Field: "../fields/QuestionnaireField" },
            properties: {
                // ── Formación y Experiencia ─────────────────────────
                degree: {
                    name: "Carrera / Titulación principal",
                    type: "array",
                    of: { name: "Carrera", type: "string" },
                    description: "Ej. Ingeniería Ambiental, Derecho, Administración"
                },
                other_studies: {
                    name: "Otros estudios",
                    type: "array",
                    of: { name: "Estudio", type: "string" },
                    description: "Maestrías, diplomados, cursos relevantes"
                },
                years_experience_total: {
                    name: "Años de experiencia total",
                    type: "string",
                    enum: [
                        { id: "0-1", label: "0 – 1 año" },
                        { id: "2-3", label: "2 – 3 años" },
                        { id: "4-6", label: "4 – 6 años" },
                        { id: "7-10", label: "7 – 10 años" },
                        { id: "10+", label: "Más de 10 años" }
                    ]
                },
                years_experience_sustainability: {
                    name: "Años de experiencia en sostenibilidad",
                    type: "string",
                    enum: [
                        { id: "0", label: "Sin experiencia" },
                        { id: "0-1", label: "Menos de 1 año" },
                        { id: "2-3", label: "2 – 3 años" },
                        { id: "4-6", label: "4 – 6 años" },
                        { id: "7-10", label: "7 – 10 años" },
                        { id: "10+", label: "Más de 10 años" }
                    ]
                },
                sustainability_start_year: {
                    name: "Año de inicio en sostenibilidad",
                    type: "number"
                },
                certifications: {
                    name: "Certificaciones relevantes",
                    type: "array",
                    of: { name: "Certificación", type: "string" },
                    description: "Ej. GRI, SASB, ISO 14001, B Corp, CDP"
                },
                /**
                 * The counterpart to a vacancy's `required_skills`. Without it
                 * a job could ask for a skill and there was nothing on the
                 * candidate to compare it against — certifications matched in
                 * one direction and skills in none.
                 *
                 * Free text like `certifications`, and normalized the same way
                 * into `skills_canon`. Some of what it will hold is already in
                 * the certifications field, where people put things that are
                 * not certifications because there was nowhere else: "qgis",
                 * "Project management", "Economía Circular", "Auditor".
                 */
                skills: {
                    name: "Habilidades",
                    type: "array",
                    of: { name: "Habilidad", type: "string" },
                    description: "Ej. Análisis de datos, Huella de carbono, Power BI, Inglés"
                },
                languages: {
                    name: "Idiomas",
                    type: "array",
                    of: {
                        name: "Idioma",
                        type: "map",
                        properties: {
                            language: { name: "Idioma", type: "string" },
                            level: {
                                name: "Nivel",
                                type: "string",
                                enum: [
                                    { id: "basic", label: "Básico" },
                                    { id: "intermediate", label: "Intermedio" },
                                    { id: "advanced", label: "Avanzado" },
                                    { id: "native", label: "Nativo" }
                                ]
                            }
                        }
                    }
                },

                // ── Áreas de Sostenibilidad ─────────────────────────
                sustainability_areas: {
                    name: "Áreas de Sostenibilidad",
                    type: "map",
                    description: "Nivel de experiencia o interés en cada área",
                    // The candidate answered this as one screen — every area on
                    // a row, graded with chips — and the generic map field
                    // turned it into 17 stacked dropdowns. Same answer, read
                    // the same way it was given.
                    //
                    // The field also renders the keys this map does not declare.
                    // An older questionnaire wrote English ones and they are
                    // still live, and a map field shows only what is declared,
                    // so those answers were in the row and on no screen.
                    //
                    // It writes `sustainability_areas_other_text` too, which is
                    // hidden from the form below for that reason.
                    admin: { Field: "../fields/SustainabilityAreasField" },
                    properties: {
                        estrategia_esg: {
                            name: "Estrategia ESG/Sostenibilidad",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        informes_sostenibilidad: {
                            name: "Informes Sostenibilidad",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        gestion_gobernanza: {
                            name: "Gestión gobernanza",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        compliance: {
                            name: "Compliance",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        materialidad: {
                            name: "Materialidad",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        cambio_climatico: {
                            name: "Cambio climático",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        energia: {
                            name: "Energía",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        agua: {
                            name: "Agua",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        economia_circular: {
                            name: "Economía circular",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        biodiversidad: {
                            name: "Biodiversidad",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        impacto_social: {
                            name: "Impacto social",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        dei: {
                            name: "Diversidad, equidad e inclusión",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        derechos_humanos: {
                            name: "Derechos Humanos",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        finanzas_sostenibles: {
                            name: "Finanzas sostenibles",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        inversion_impacto: {
                            name: "Inversión de impacto",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        cadena_suministro_sostenible: {
                            name: "Cadena de suministro sostenible",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        },
                        otro: {
                            name: "OTRO",
                            type: "string",
                            enum: [
                                { id: "none", label: "Sin experiencia", color: "gray" },
                                { id: "interested", label: "Interesado/a", color: "blue" },
                                { id: "beginner", label: "Principiante", color: "teal" },
                                { id: "intermediate", label: "Intermedio", color: "orange" },
                                { id: "advanced", label: "Avanzado", color: "green" }
                            ]
                        }
                    }
                },

                // ── Ubicación y Disponibilidad ──────────────────────
                country: {
                    name: "País de residencia",
                    type: "string",
                    /**
                     * Filtering on this column does NOT find everyone in a
                     * country, and there is no way to make it, because the
                     * filter is an equality on the stored enum.
                     *
                     * It is empty on every row imported before the wizard asked
                     * and on everyone who registered and never finished — and
                     * most of those wrote the country into "Ciudad de
                     * residencia" instead ("Santiago, Chile"). Today that is 63
                     * of 194 Mexicans and 4 of 13 Chileans: a third of the
                     * base, invisible to this filter, with no sign that
                     * anything is missing.
                     *
                     * `search_talents` in the MCP server resolves both fields
                     * (`admin-app/backend/src/services/location-text.ts`) and
                     * reports how many rows it could not place. Until this
                     * column has a resolved counterpart in the database, that
                     * is the tool to use before telling a client we reviewed
                     * everyone somewhere — and the description below says so on
                     * the screen, where the mistake gets made.
                     */
                    description: "Solo lo contestó quien completó el cuestionario. Filtrar por este campo NO " +
                        "encuentra a todo el mundo: en el resto de fichas el país está escrito dentro de " +
                        "«Ciudad de residencia». Para un recuento por país, usa `search_talents`.",
                    enum: [
                        { id: "España", label: "España" },
                        { id: "México", label: "México" },
                        { id: "Colombia", label: "Colombia" },
                        { id: "Chile", label: "Chile" },
                        { id: "Argentina", label: "Argentina" },
                        { id: "Perú", label: "Perú" },
                        { id: "Uruguay", label: "Uruguay" },
                        { id: "Bolivia", label: "Bolivia" },
                        { id: "Paraguay", label: "Paraguay" },
                        { id: "Costa Rica", label: "Costa Rica" },
                        { id: "Guatemala", label: "Guatemala" },
                        { id: "República Dominicana", label: "República Dominicana" },
                        { id: "Panamá", label: "Panamá" },
                        { id: "Ecuador", label: "Ecuador" },
                        { id: "El Salvador", label: "El Salvador" },
                        { id: "Honduras", label: "Honduras" },
                        { id: "Nicaragua", label: "Nicaragua" },
                        { id: "Venezuela", label: "Venezuela" },
                        { id: "Puerto Rico", label: "Puerto Rico" },
                        { id: "Estados Unidos", label: "Estados Unidos" },
                        { id: "Otro", label: "Otro" }
                    ]
                },
                city: {
                    name: "Ciudad de residencia",
                    type: "string"
                },
                preferred_work_model: {
                    name: "Modalidad de trabajo preferida",
                    type: "array",
                    of: {
                        name: "Modalidad",
                        type: "string",
                        enum: [
                            { id: "on_site", label: "Presencial", color: "orange" },
                            { id: "hybrid", label: "Híbrido", color: "teal" },
                            { id: "remote", label: "100% Remoto", color: "blue" },
                            { id: "any", label: "Cualquiera", color: "green" }
                        ]
                    }
                },
                travel_availability: {
                    name: "Disponibilidad para viajar",
                    type: "string",
                    enum: [
                        { id: "none", label: "Nula" },
                        { id: "occasional", label: "Ocasional" },
                        { id: "frequent", label: "Frecuente" }
                    ]
                },
                relocation_available: {
                    name: "Disponibilidad para reubicarse",
                    type: "string",
                    enum: [
                        { id: "yes", label: "Sí" },
                        { id: "no", label: "No" },
                        { id: "conditional", label: "Solo bajo ciertas condiciones" }
                    ]
                },
                relocation_conditions: {
                    name: "Condiciones para reubicación",
                    type: "string",
                    admin: {
                        multiline: true
                    },
                    description: "Apoyo económico, visado, ciudades específicas, etc."
                },

                // ── Expectativas Salariales y Profesionales ─────────
                //
                // "anual" is gone from both names. It was never true of half
                // the answers: the wizard printed that word over an example of
                // `40000`, which in Mexico is a month, and the column ended up
                // holding both units with nothing saying which. `salary_period`
                // is the field that settles it, and the three read-only ones
                // below are what `backfill-salary-period.ts` worked out for the
                // answers given before it existed.
                salary_period: {
                    name: "¿La cifra es mensual o anual?",
                    type: "string",
                    enum: [
                        { id: "mensual", label: "Mensual" },
                        { id: "anual", label: "Anual" }
                    ],
                    description: "Lo que la persona eligió al responder. Vacío en las respuestas anteriores a la pregunta."
                },
                salary_min: {
                    name: "Salario bruto mínimo aceptable",
                    type: "number"
                },
                salary_ideal: {
                    name: "Salario bruto ideal",
                    type: "number"
                },
                salary_currency: {
                    name: "Moneda",
                    type: "string",
                    enum: [
                        { id: "USD", label: "USD" },
                        { id: "EUR", label: "EUR" },
                        { id: "MXN", label: "MXN" },
                        { id: "COP", label: "COP" },
                        { id: "CLP", label: "CLP" },
                        { id: "BRL", label: "BRL" },
                        { id: "ARS", label: "ARS" },
                        { id: "PEN", label: "PEN" },
                        { id: "UYU", label: "UYU" },
                        { id: "BOB", label: "BOB" },
                        { id: "PYG", label: "PYG" },
                        { id: "CRC", label: "CRC" },
                        { id: "GTQ", label: "GTQ" },
                        { id: "DOP", label: "DOP" },
                        { id: "PAB", label: "PAB" },
                        { id: "GBP", label: "GBP" },
                        { id: "other", label: "Otra" }
                    ]
                },
                // Derived, never typed. Editing one here would put a number
                // beside the real ones with nothing to say where it came from,
                // which is the failure the whole exercise is undoing.
                salary_monthly_min: {
                    name: "Mínimo al mes (calculado)",
                    type: "number",
                    admin: { readOnly: true },
                    description: "En la moneda de la respuesta. Vacío cuando no se pudo determinar la unidad."
                },
                salary_monthly_ideal: {
                    name: "Ideal al mes (calculado)",
                    type: "number",
                    admin: { readOnly: true },
                    description: "En la moneda de la respuesta. Vacío cuando no se pudo determinar la unidad."
                },
                salary_unit_source: {
                    name: "Origen de la cifra mensual",
                    type: "string",
                    admin: { readOnly: true },
                    enum: [
                        { id: "declared", label: "Lo dijo la persona" },
                        { id: "inferred", label: "Deducido de la respuesta" },
                        { id: "unresolved", label: "No se pudo determinar" }
                    ]
                },
                // Declared so the collection knows the key exists, hidden so it
                // does not clutter a form nobody fills in by hand. Every other
                // key stored in this jsonb is declared here; an undeclared one
                // is a key nothing in the panel can account for, which is how
                // an audit trail quietly stops existing.
                salary_reading: {
                    name: "Lectura aplicada a cada cifra",
                    type: "map",
                    // `conditions`, not `admin`, and the same way
                    // `sustainability_areas_other_text` above is kept off the
                    // form — that key is declared, hidden, and still in every
                    // row, which is the proof this shape survives a save.
                    conditions: { hidden: true },
                    properties: {
                        min: { name: "Mínimo", type: "string" },
                        ideal: { name: "Ideal", type: "string" }
                    }
                },
                // What kind of work they are looking for — the same ids a
                // vacancy is filed under (config/taxonomy/job-functions.ts),
                // plus `any`, which is an answer about the person and never a
                // kind of work. Asked from September 2026 on; empty on every
                // profile before that.
                preferred_job_functions: {
                    name: "Tipo de trabajo que busca",
                    type: "array",
                    of: {
                        name: "Tipo",
                        type: "string",
                        enum: [
                            ...JOB_FUNCTIONS.map(({ id, label }) => ({ id, label })),
                            { id: ANY_JOB_FUNCTION, label: "Sin preferencia" }
                        ]
                    }
                },
                preferred_org_type: {
                    name: "Tipo de organización preferida",
                    type: "array",
                    of: {
                        name: "Tipo",
                        type: "string",
                        enum: [
                            { id: "startup", label: "Startup" },
                            { id: "ngo", label: "ONG / Tercer Sector" },
                            { id: "corporate", label: "Corporativo Multinacional" },
                            { id: "consultancy", label: "Consultora" },
                            { id: "public_sector", label: "Sector Público" },
                            { id: "any", label: "Sin preferencia" }
                        ]
                    }
                },
                sustainability_areas_other_text: {
                    name: "Especificación de otra especialidad ESG",
                    type: "string",
                    conditions: { hidden: true }
                },
                career_motivation: {
                    name: "Principales logros profesionales",
                    type: "string",
                    admin: {
                        multiline: true
                    },
                    description: "Cuéntanos tus principales logros profesionales"
                }
            }
        },
        questionnaire_completed_at: {
            name: "Cuestionario Completado",
            type: "date",
            description: "Fecha en que el candidato completó el cuestionario"
        },
        /**
         * The candidate's own word on whether the profile still holds, with a
         * date on it — see drizzle/0046. `questionnaire_completed_at` above
         * cannot do this job: it is written once, on first completion, and the
         * profile editor has never touched it.
         *
         * readOnly, and not because the form would break. A value here means
         * *the person* said so, from one of three places they act in: the
         * questionnaire, the profile editor, or the confirm button. A recruiter
         * typing a date into this field would be asserting something only the
         * candidate can assert, which is the whole failure this replaces.
         * Correcting the profile on someone's behalf is what the other fields
         * are for; it is not a confirmation, and it must not look like one.
         *
         * Empty means nobody ever confirmed it — `cv_import` and `manual` rows
         * were built without the candidate — not that the date is unknown.
         */
        linkedin_linked_at: {
            name: "LinkedIn conectado",
            type: "date",
            description: "Cuándo la cuenta ganó una identidad de LinkedIn. Vacío = no conectada. Espejo de rebase.user_identities; ninguna colección puede leer esa tabla.",
            admin: { readOnly: true }
        },
        profile_confirmed_at: {
            name: "Confirmado por el candidato",
            type: "date",
            description: "Última vez que el propio candidato dio el perfil por bueno. Vacío = nunca lo confirmó; sólo lo escriben el cuestionario, el editor de perfil y el botón de confirmar.",
            admin: { readOnly: true }
        },
        /**
         * What we have sent this person, and whether the address still works
         * (drizzle/0057).
         *
         * The three dates above say what the *candidate* did. These four say
         * what *we* did, and they are kept apart from them for the reason 0046
         * gives at length: a value the person authored and a value we wrote are
         * different claims, and one screen showing both must be able to say
         * which is which.
         *
         * All four are readOnly except `email_bounced_at`, and none of them is
         * a preference: whether somebody wants the newsletter is a row in
         * `digest_subscriptions`, not a field here. What lives here is only the
         * record of a message leaving — written after the mail server accepted
         * it, never when it was merely attempted.
         */
        profile_nudge_sent_at: {
            name: "Aviso de perfil enviado",
            type: "date",
            description: "Cuándo le preguntamos por última vez si su perfil sigue vigente. Lo escribe el envío automático, y sólo si el correo salió.",
            admin: { readOnly: true }
        },
        profile_nudge_reminded_at: {
            name: "Recordatorio enviado",
            type: "date",
            description: "El único recordatorio, cuando el aviso no tuvo respuesta. Después de éste no se le vuelve a escribir hasta que confirme su perfil.",
            admin: { readOnly: true }
        },
        /**
         * The one of the four a person may set, because a person is often the
         * one who finds out: a bounce lands in the shared inbox as a message
         * from a mail server, and whoever reads it is the only party that knows
         * the address is dead until the next send tries it again.
         *
         * Not readOnly, therefore — and the description says what setting it
         * does, because it is the one field on this record that silently stops
         * a candidate from ever hearing from us again.
         */
        email_bounced_at: {
            name: "Email rebotado",
            type: "date",
            description: "La dirección dejó de aceptar correo. Con una fecha aquí no se le vuelve a escribir nada automático — ni avisos de perfil ni boletín. Bórrala sólo si la dirección se ha corregido."
        },
        digest_invited_at: {
            name: "Invitación al boletín enviada",
            type: "date",
            description: "Cuándo se le invitó al boletín de vacantes. Es el registro de la invitación, no de la respuesta: si la aceptó, tiene una suscripción.",
            admin: { readOnly: true }
        },
        /**
         * Derived from `questionnaire` by Postgres, not by anything here.
         *
         * These exist so the triage queue can be ordered and filtered by the
         * database. Before them the board fetched the whole roster with
         * `limit: 1000` and ranked it in memory — correct at 297 candidates and
         * silently a subset at 1001, with tab counts to match.
         *
         * All six are `GENERATED ALWAYS AS … STORED` (drizzle/0025): Postgres
         * recomputes them whenever `questionnaire` changes and rejects any
         * attempt to write them, which is why they are readOnly here — the form
         * must not offer what the database will refuse. The rules they encode
         * are the ones in views/_talent_taxonomy.ts, checked row-by-row against
         * it by scripts/check-strength-parity.mjs.
         */
        strength_score: {
            name: "Puesto en la cola",
            type: "number",
            description: "Orden de la cola: sin inflar, luego áreas avanzadas, sólidas, experiencia y cuestionario completo. Sólo sirve para ordenar.",
            admin: { readOnly: true }
        },
        advanced_area_count: {
            name: "Áreas avanzadas",
            type: "number",
            admin: { readOnly: true }
        },
        strong_area_count: {
            name: "Áreas sólidas",
            type: "number",
            description: "Intermedio o superior",
            admin: { readOnly: true }
        },
        experience_floor: {
            name: "Años (mínimo)",
            type: "number",
            description: "Extremo inferior del rango declarado",
            admin: { readOnly: true }
        },
        profile_inflated: {
            name: "Se declara avanzado en casi todo",
            type: "boolean",
            description: "Se declara avanzado en la mitad del vocabulario o más",
            admin: { readOnly: true }
        },
        area_keys: {
            name: "Áreas",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Área" },
            description: "Áreas declaradas, normalizadas — para filtrar",
            admin: { readOnly: true },
            // Out of the form, kept in the table. It is the normalized key list
            // the filters run on, so it renders as `cadena_suministro_sostenible`
            // — and it sat directly above the questionnaire's own areas, which
            // say the same thing in Spanish and with the level attached. Two
            // readings of one answer, one of them in identifiers.
            conditions: { hidden: true }
        },
        /**
         * The certifications and skills this person claims, normalized.
         *
         * Same job as `area_keys` directly above and the same treatment —
         * normalized identifiers, out of the form, kept for filtering — but not
         * the same mechanism. Those six columns are `GENERATED ALWAYS AS …
         * STORED`, because the rules behind them are expressible in SQL. These
         * two are not: the vocabulary, its aliases and the ISO rule live in
         * `taxonomy/credentials.ts`, so `beforeSave` writes them instead.
         *
         * The raw answers stay in `questionnaire.certifications` and
         * `questionnaire.skills`, which is what the person actually typed and
         * what the panel shows them. These are only an index of it: 78% of
         * certification entries normalize, and the rest — courses, job titles,
         * one "cvfrvgtfv" — keep their text and match nothing, which is the
         * right answer for all three.
         */
        certifications_canon: {
            name: "Certificaciones (normalizadas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            description: "Certificaciones reconocidas del texto libre — para filtrar y cruzar con vacantes",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        skills_canon: {
            name: "Habilidades (normalizadas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            description: "Habilidades reconocidas del texto libre — para filtrar y cruzar con vacantes",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        /**
         * The skills nobody asked this person about, worked out from what they
         * did write. See drizzle/0041 for why it is a separate column.
         *
         * The short version: `skills_canon` means *the candidate said so*, and
         * 0 of the 308 candidates in the database were ever shown the skills
         * question — it shipped after they filled the form. Writing inferences
         * into that column would make a recruiter's reading of a green chip
         * false for every row, and the next profile save would wipe them anyway,
         * since `deriveTalentCanon` recomputes it from the questionnaire alone.
         *
         * Read together with `skills_canon` when the question is "can they do
         * X"; shown apart when the question is "who says so".
         */
        skills_inferred: {
            name: "Habilidades (deducidas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            description: "Deducidas de lo que la persona escribió en otros campos. No las declaró: nunca se le preguntó.",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        skills_inferred_at: {
            name: "Habilidades deducidas el",
            type: "date",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        skills_inferred_model: {
            name: "Deducidas por",
            type: "string",
            description: "El modelo que las produjo, o 'catalogo' para la pasada determinista",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        /**
         * How this person got into the database.
         *
         * The two populations are not interchangeable and the list gave no way
         * to tell them apart. Someone who registered chose to be here, agreed
         * to the privacy policy and answered the questionnaire themselves;
         * someone imported from a CV did none of those things — the record is
         * the agency's reading of a document, the questionnaire is whatever the
         * model could infer, and there is no account behind it. Contacting the
         * second as though they had applied is the mistake this prevents.
         *
         * `auth_user_id` almost tells you: a registered candidate has one. But
         * it is null for anyone captured before they registered, so its absence
         * means "no account yet", not "we imported this". Recording the origin
         * says which, and keeps saying it after an account is linked.
         */
        tags: {
            name: "Etiquetas",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Etiqueta" },
            description: "Etiquetas libres del equipo: una terna, un interés, un aviso. En minúsculas; nunca las ve el candidato.",
            defaultValue: []
        },
        origin: {
            name: "Origen",
            type: "string",
            enum: [
                { id: "signup", label: "Se registró", color: "green" },
                { id: "cv_import", label: "Importado de un CV", color: "orange" },
                // They wrote to us — usually to apply, usually with a CV
                // attached — and an operator created the record from Correo.
                // Distinct from `cv_import` because the person *did* act:
                // contacting them about the vacancy they named is not the
                // mistake `origin` exists to prevent.
                { id: "email", label: "Escribió por correo", color: "cyan" },
                { id: "manual", label: "Creado a mano", color: "grayLight" }
            ],
            defaultValue: "manual",
            admin: { readOnly: true },
            description: "Cómo llegó esta persona a la base. No se edita: es un hecho sobre el registro, no sobre la persona."
        },
        lead_status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "new", label: "Sin revisar", color: "blue" },
                { id: "contacted", label: "Contactado", color: "orange" },
                // Id stays `placed`; "colocado" is recruitment jargon that means
                // nothing outside the trade. `TALENT_TABS` in
                // config/views/_talents_shared.ts uses the same wording.
                { id: "placed", label: "Contratado", color: "green" }
            ],
            defaultValue: "new"
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },

        // The one tab on a candidate, and the only connection this panel draws
        // between a person and a vacancy: the ones they applied to themselves.
        //
        // Was a `via` join across `auth_user_id`, because the application row
        // had no `talent_id` to point back with. drizzle/0029 added one, so this
        // is a plain foreign key now — which also means the tab and the
        // "Postulaciones" collection agree about what links them.
        "talent-applications": {
            name: "Vacantes a las que postuló",
            type: "relation",
            relation: {
                kind: "hasMany",
                target: () => talentApplicationsCollection,
                foreignKeyOnTarget: "talent_id"
            },
            // Tab yes, form field no — see `_relation_display.ts`.
            conditions: HIDDEN_FROM_FORM
        }
    },
    admin: {
        group: "Personas",
        icon: "Users",
        /**
         * Full screen, not a side panel.
         *
         * On 2026-09-06 a vacancy opened in a strip about 180 px wide beside
         * the table — the title wrapped four times and the cover did not fit —
         * and nothing said the panel could be widened. A record's cover is a
         * page: "Lo siguiente", the profile, the correspondence. The queue
         * views keep their own side panel over the queue (see
         * `collectionViews` in frontend/src/App.tsx); this is the list.
         */
        openEntityMode: "full_screen",
        /**
         * One entry for candidates, with the queue as a view mode of it.
         *
         * There were two: this table, and a top-level `AppView` at `/candidatos`
         * holding the queue. Both were about the same ~300 rows — the workspace
         * was `talents.map(...)` with applications joined in — so the drawer
         * offered a choice between "the workspace" and "the archive", which is a
         * distinction this repo makes and nobody using it does.
         *
         * Renaming them did not help and neither did hiding one. What was
         * actually wrong is that the queue is a *rendering* of this collection
         * and the only place to put a custom rendering was a separate screen.
         * Rebase 0.15 opened `ViewMode`, so it goes where it belongs: "Cola" is
         * now a view mode beside "Lista" and "Tabla", switched from the
         * collection's own toolbar.
         *
         * The key is a string, not the component: this file is imported by the
         * backend for its schema, and a React import here pulls the admin bundle
         * into it. `frontend/src/App.tsx` registers the component against this
         * key via `collectionViews`, the same way `Actions` and `entityViews`
         * are registered.
         *
         * Why the queue is still a custom component and not `filterPresets` +
         * `sort`: both things it orders by — whether a person has an open
         * application, and how long the oldest one has waited — live in
         * `talent_applications`, and 0.15 ships no way to filter or sort a
         * collection by a column of a related table. Until it does, that
         * ordering can only be computed after the rows are read. See
         * `_talents_shared.ts`.
         */
        customViews: ["cola"],
        defaultViewMode: "cola",
        /**
         * A candidate opens on their cover, not on the form.
         *
         * `resumen` is registered in `frontend/src/App.tsx` and drawn by
         * `views/TalentOverview.tsx` — the same component the queue's detail
         * pane uses. Without this the record opened on twenty-odd inputs,
         * which is the screen for *correcting* a person and not the one for
         * reading them, and the reading surface the queue already had stopped
         * at the edge of the queue.
         *
         * The key is a string here and the component is registered against it
         * in the frontend, for the same reason `customViews` is: the backend
         * imports this file for its schema and must not pull in a tree of JSX.
         *
         * A builder rather than the bare key, for the one case where the cover
         * is the wrong answer: a candidate being *created* has no id yet, so
         * their applications, their correspondence and the status button all
         * point at a row that does not exist. "Añadir Candidato" opens the form,
         * which is the only thing there is to do with a new one.
         */
        defaultSelectedView: ({ status }) => (status === "new" ? undefined : "resumen"),
        // "cards" is omitted, not forgotten: a candidate has no image, so the
        // card view draws 300 identical grey placeholders.
        //
        // List before table for the same reason as before — a candidate is a
        // person, and the row that names one reads better than 20 columns of
        // which the first two are uuids.
        enabledViews: ["cola", "list", "table"],
        // Newest registrations first. The presets below keep ordering by
        // strength, since those are for triaging a batch, not for seeing who
        // just arrived.
        sort: ["created_at", "desc"],
        // Kept deliberately short, because the list view renders these as
        // columns *without headers*. `strength_score` was here and printed
        // 104161001 on every row — a packed sort key that reads as an account
        // number. Replacing it with the three counts it is built from only
        // traded one meaningless number for three: "4 · 16 · 10" over an
        // unlabelled column is not more legible than the score was.
        //
        // What those numbers actually say now lives in `display.subtitle`,
        // where it can be a sentence. They remain columns in table mode via the
        // column picker, and on the candidate's own page.
        listProperties: [
            "full_name",
            "origin",
            "lead_status",
            "questionnaire_completed_at"
        ],
        display: {
            title: "full_name",
            // Where they are and how deep they go, as a sentence. This is the
            // one thing the list is for, and it cannot be a declared path,
            // because it is four generated columns and a location combined.
            //
            // Every value it reads is on the row already (drizzle/0025 made them
            // real columns), so this costs no request. Parts that are null drop
            // out rather than printing "0 áreas avanzadas", which would state a
            // fact about someone who simply never filled the questionnaire.
            subtitle: ({ entity }) => {
                const v = entity.values as {
                    location?: string | null;
                    advanced_area_count?: number | null;
                    strong_area_count?: number | null;
                    experience_floor?: number | null;
                    profile_inflated?: boolean | null;
                    questionnaire_completed_at?: string | null;
                };
                const parts: string[] = [];
                if (v.location) parts.push(v.location);
                if (!v.questionnaire_completed_at) {
                    parts.push("cuestionario sin terminar");
                } else if (v.profile_inflated) {
                    // Says so plainly instead of quoting a count nobody believes:
                    // claiming "avanzado" across half a 17-area vocabulary is the
                    // inflated-profile shape, and the count alone reads as a
                    // strength.
                    parts.push("se declara avanzado en casi todo");
                } else {
                    if (v.advanced_area_count) parts.push(`${v.advanced_area_count} áreas avanzadas`);
                    else if (v.strong_area_count) parts.push(`${v.strong_area_count} áreas intermedias`);
                    if (v.experience_floor) parts.push(`${v.experience_floor}+ años`);
                }
                return parts.join(" · ") || undefined;
            },
            status: "lead_status",
            date: "created_at",
            // The areas a candidate claims, straight off the generated column.
            // What is lost against the custom card: the chips carried a colour
            // per level (green advanced, orange intermediate) and here they are
            // all one weight, because `area_keys` is a flat text[] with the
            // level collapsed out of it.
            tags: "area_keys"
        },
        filterPresets: [
            {
                label: "Sin revisar",
                filterValues: { lead_status: ["==", "new"] },
                sort: ["strength_score", "desc"]
            },
            {
                label: "Cuestionario completo",
                filterValues: { questionnaire_completed_at: ["is-not-null", null] },
                sort: ["strength_score", "desc"]
            },
            {
                // Was "Perfiles inflados", which is our word for them and not
                // a description of anything. The card under each name already
                // says "se declara avanzado en casi todo"; the filter says the
                // same thing, so the two are recognisably the same fact.
                label: "Se declaran avanzados en casi todo",
                filterValues: { profile_inflated: ["==", true] }
            }
        ],
        previewProperties: ["full_name", "email", "lead_status"],
        // The questionnaire is a deep nested map — the bulk of the record and
        // the last thing you need when correcting a phone number. Giving it its
        // own section keeps contact details reachable without scrolling past it,
        // and the pipeline state moves to the rail where it is not an edit.
        form: {
            // The four 0057 dates sit directly under the confirmation they are
            // measured against: "confirmó en abril, le avisamos en octubre, el
            // recordatorio salió en noviembre" is one sentence read top to
            // bottom, and splitting it across the rail and the form would make
            // it two facts nobody joins. `email_bounced_at` is the only one of
            // them a person can type, and it belongs beside the others rather
            // than among the contact fields: it is not a property of the
            // address, it is the reason we stopped using it.
            sidebar: [
                "lead_status",
                "questionnaire_completed_at",
                "profile_confirmed_at",
                "profile_nudge_sent_at",
                "profile_nudge_reminded_at",
                "email_bounced_at",
                "digest_invited_at",
                "auth_user_id"
            ],
            sections: [
                {
                    key: "person",
                    properties: ["full_name", "email", "phone", "location"]
                },
                {
                    key: "links",
                    title: "Enlaces",
                    properties: ["linkedin_url", "cv_url"]
                },
                {
                    key: "intent",
                    title: "Objetivo profesional",
                    properties: ["professional_objective", "interests"]
                },
                {
                    key: "questionnaire",
                    title: "Cuestionario",
                    // Open. It was collapsed because an unfinished questionnaire
                    // is an empty 40-field map, but the answers are the reason
                    // anyone opens a candidate, and a click stood between the
                    // reviewer and all of them. The 100 rows with nothing in it
                    // cost a scroll past a short empty section; the 138 with
                    // answers were the ones being hidden.
                    properties: ["questionnaire", "privacy_accepted"]
                }
            ]
        }
    }
};

export default talentsCollection;
